import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import {
    GRID_FIXED_PADDING,
    Layout
} from "@/libraries/wpl/foundations/src/grid";
import { GlobalFooter } from "@/patterns/global-footer/src/global-footer";
import {
    PaidSocialProof,
    PaidSocialProofProps
} from "@/patterns/paid-landings/paid-social-proof/src/paid-social-proof";
import { ThreeAlternatingImageText } from "@/patterns/three-alternating-image-text/src/three-alternating-image-text";
import { PatternCTAMobileProps } from "@/patterns/wpl/cta-primary/src/cta-primary";
import { PaidCtaSection } from "@/templates/paid-landing/sections/paid-cta-section.tsx";
import { PaidTabsSection } from "@/templates/paid-landing/sections/paid-tabs-section.tsx";
import { TabsProps } from "@/templates/paid-landing/src/paid-landing.template.tsx";
import { ParentScrollTracking } from "@/utils/scroll-tracking/parent-scroll-tracker";

import {
    Report,
    Collaborate,
    Plan,
    Track
} from "../../../../pages/software/jira/anchors/paid-lp-jsw-features.tsx";

const jiraAiBodyStyles = css({
    paddingBlockStart: "100px"
});

const threeAlternatingImageTextCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".row-layout": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [media.above.md]: {
            width: `${(1168 + GRID_FIXED_PADDING * 2) / 16}rem`
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".text-section h2": {
        fontWeight: 700,
        fontSize: "2.5rem",
        lineHeight: "3rem",
        color: wacToken("color.primary.black")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".text-section p": {
        fontSize: "1.25rem",
        lineHeight: "1.75rem",
        color: wacToken("color.primary.black")
    }
});

const paidCtaSectionCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".text-section": {
        maxWidth: "34rem",
        margin: "0 auto"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".text-section h2": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#000",
        fontWeight: "bold"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".contentBlock-linkFlex a": {
        fontSize: "1.25rem",
        lineHeight: "1.5rem",
        borderRadius: "2.5rem",
        padding: "0.5rem 1.5rem",
        height: "2.5rem"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".text-section > *:last-child": {
        marginBlockEnd: 0
    }
});

export const PaidJiraAiBody = () => {
    const intl = useIntl();

    const ctaButtonPropsHref = useLocalizedHref(
        "/try/cloud/signup?bundle=jira-software&edition=free"
    );
    const landingType = "jira-software-paid-ai";
    const tabsProps: TabsProps = {
        dataProductKey: "jiraSoftware-PaidLpJiraFfl",
        headerText: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.heading",
            defaultMessage: "Discover AI features that power your teams"
        }),
        tabStyles: {
            defaultBG: wacToken("color.neutral.light0"),

            defaultText: wacToken("color.extended.blue1000"),
            hoverBG: wacToken("color.neutral.light0"),

            hoverText: wacToken("color.extended.blue1000"),

            activeBG: wacToken("color.extended.blue200"),

            activeText: wacToken("color.extended.blue1000")
        },
        styles: {
            "--lp-tabs-hover": wacToken("color.extended.blue200"),
            "--lp-tabs-unselected": wacToken("color.neutral.light700")
        },
        tabs: [
            {
                textLabel: intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab1.textLabel",
                    defaultMessage: "Plan"
                }),
                id: "plan",
                component: Plan()
            },
            {
                textLabel: intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab2.textLabel",
                    defaultMessage: "Track"
                }),
                id: "track",
                component: Track()
            },
            {
                textLabel: intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab3.textLabel",
                    defaultMessage: "Collaborate"
                }),
                id: "collaborate",
                component: Collaborate()
            },
            {
                textLabel: intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswFeaturesGwp9473Updates.tabs.tab4.textLabel",
                    defaultMessage: "Report"
                }),
                id: "report",
                component: Report()
            }
        ]
    };
    const alternatingTextAndImageProps = {
        backgroundColor: wacToken("color.extended.blue100"),
        imageWidth: "100%",
        imageTextSideRowOnePadding: "7.5rem 0 1rem",
        imageTextSideRowOnePaddingBelowLargeScreen: "7.5rem 0 1rem",
        imageTextSideRowTwoPadding: "1rem 0",
        imageTextSideRowThreePadding: "1rem 0 7.5rem",
        imageTextSideRowThreePaddingBelowLargeScreen: "1rem 0 7.5rem",
        rowPropsOdd: "7-5" as Layout,
        rowPropsEven: "5-7" as Layout,
        //Row 1
        rowOneHeader: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowOneHeader",
            defaultMessage: "Customize how your team’s work flows"
        }),
        rowOneBody: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowOneBody",
            defaultMessage:
                "Set up, clean up, and automate even the most complicated project workflows."
        }),
        rowOneImageLink:
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EZD9.webp",
        rowOneImageAltText: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowOneImageAltText",
            defaultMessage: "Jira boards"
        }),
        //Row 2
        rowTwoHeader: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.secondTextHeader",
            defaultMessage: "Stay on track – even when the track changes"
        }),
        rowTwoBody: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.secondTextBody",
            defaultMessage:
                "Use the timeline view to map out the big picture, communicate updates to stakeholders, and ensure your team stays on the same page."
        }),
        rowTwoImageLink:
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EZD7.webp",
        rowTwoImageAltText: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowTwoImageAltText",
            defaultMessage: "Jira timeline view."
        }),
        //Row 3
        rowThreeHeader: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowThreeHeader",
            defaultMessage: "Bye-bye, spreadsheets"
        }),
        rowThreeBody: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowThreeBody",
            defaultMessage:
                "Keep every detail of a project centralized in real time so up-to-date info can flow freely across people, teams, and tools."
        }),
        rowThreeImageLink:
            "https://dam-cdn.atl.orangelogic.com/CDNLink/AT12EZD8.webp",
        rowThreeImageAltText: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeatures.alternatingTextAndImage.rowThreeImageAltText",
            defaultMessage: "Spreadsheets"
        })
    };
    const ctaProps: PatternCTAMobileProps = {
        headerText: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeatures.ctaSection.header",
            defaultMessage:
                "Move fast, stay aligned, and build better - together"
        }),
        ctaProps: {
            buttonProps: {
                color: "blueprimary700",
                url: ctaButtonPropsHref,
                text: intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswFeatures.ctaSection.button",
                    defaultMessage: "Get it free"
                }),
                dataEventProps: {
                    "data-event": "clicked",
                    "data-event-container": "ctaSection",
                    "data-event-component": "linkButton",
                    "data-product-key": "jiraSoftware-PaidLpJiraFeatures"
                },
                size: "small"
            }
        },
        backgroundProperties: {
            textColor: wacToken("color.neutral.light0")
        }
    };
    const testimonialProps: PaidSocialProofProps = {
        headerText: intl.formatMessage({
            id: "JiraSoftwarePagePaidLpJswFeatures.socialProof.heading",
            defaultMessage: "Trusted by over 100,000 customers world-wide"
        }),
        logos: [
            {
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/5382nib232306760cxko3fu33yr03hc6.svg",
                alt: intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswFeatures.socialProof.squareAlt",
                    defaultMessage: "Square logo"
                }),
                loading: "lazy",
                width: 162,
                height: 40
            },
            {
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/8sh6x4kh58c7576am3gc70215abr77gu.svg",
                alt: intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswFeatures.socialProof.ebayAlt",
                    defaultMessage: "eBay logo"
                }),
                loading: "lazy",
                width: 99,
                height: 41
            },
            {
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/7l40fo7ky3ck1752eh0ti31556343388.svg",
                alt: intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswFeatures.socialProof.spotifyAlt",
                    defaultMessage: "Spotify logo"
                }),
                loading: "lazy",
                width: 163,
                height: 49
            },
            {
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/0atd5rxku6yg42wfhx0b1jdrsusm532i.svg",
                alt: intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswFeatures.socialProof.ciscoAlt",
                    defaultMessage: "Cisco logo"
                }),
                loading: "lazy",
                width: 92,
                height: 49
            }
        ]
    };

    return (
        <div css={jiraAiBodyStyles}>
            <ParentScrollTracking
                pageId="paid-lp-jsw-ffl"
                scrollTrackingEnabled
            >
                <PaidTabsSection {...tabsProps} />
                <div css={threeAlternatingImageTextCss}>
                    <ThreeAlternatingImageText
                        {...alternatingTextAndImageProps}
                    />
                </div>
                <div css={paidCtaSectionCss}>
                    <PaidCtaSection
                        ctaProps={ctaProps}
                        landingType={landingType}
                    />
                </div>
                <PaidSocialProof {...testimonialProps} />
                <GlobalFooter />
            </ParentScrollTracking>
        </div>
    );
};
